import React, { useContext, forwardRef } from 'react';
import { Filter as FilterIcon } from 'react-feather';
import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { shouldForwardProp } from '../../utils/emotion';

export type FilterButtonVariant = 'chart' | 'analysis' | 'survey' | 'survey-response';

// Flag to enable white labeling for analysis variant, set to true if needed
// Added this in case Product wants to apply further white labeling in the future from what they have asked currently
const ENABLE_ANALYSIS_WHITE_LABELING = false;

type Props = {
  onClick?: () => void;
  disabled?: boolean;
  count?: number;
  variant?: FilterButtonVariant;
  className?: string;
  'aria-label'?: string;
  type?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
};

export const FilterButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    onClick,
    disabled = false,
    count = 0,
    variant = 'chart',
    className,
    'aria-label': ariaLabel = 'Filter',
    type = 'button',
    children: _children,
  } = props;

  const isActive = count > 0;
  const themingContext = useContext(AppThemingContext);

  const enabled = themingContext?.enabled;
  const themeColor = enabled ? themingContext.palette?.primary?.main : null;
  const hasCustomColor = !!themeColor;

  if (variant === 'analysis') {
    return renderAnalysisVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor });
  }

  if (variant === 'survey') {
    return renderSurveyVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count });
  }

  if (variant === 'survey-response') {
    return renderSurveyResponseVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count });
  }

  // Default chart variant
  return renderChartVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count });
});

type VariantProps = {
  ref: React.Ref<HTMLButtonElement>;
  onClick?: () => void;
  disabled: boolean;
  ariaLabel: string;
  type: 'button' | 'submit' | 'reset';
  className?: string;
  isActive: boolean;
  hasCustomColor: boolean;
  themeColor: string | null;
  count?: number;
};

function renderAnalysisVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count }: VariantProps) {
  const analysisThemeColor = ENABLE_ANALYSIS_WHITE_LABELING && hasCustomColor ? themeColor : null;
  const dotThemeColor = hasCustomColor ? themeColor : 'rgb(40, 170, 225)';

  return (
    <AnalysisButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={className}
      $themeColor={analysisThemeColor}>
      <AnalysisIconContainer>
        <StyledFilterIcon
          height={20}
          width={20}
          $analysisVariant={true}
          $themeColor={analysisThemeColor} />
        <AnalysisActiveCircle
          $isActive={isActive}
          $themeColor={dotThemeColor} />
      </AnalysisIconContainer>
      <AnalysisDivider $themeColor={analysisThemeColor} />
      <AnalysisText $themeColor={analysisThemeColor}>
        Filter {isActive ? `(${count})` : ''}
      </AnalysisText>
    </AnalysisButton>
  );
}

function renderSurveyVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count }: VariantProps) {
  return (
    <SurveyButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={className}
      $isActive={isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={themeColor}>
      <SurveyIconContainer>
        <StyledFilterIcon
          height={20}
          width={20}
          $themeColor={themeColor} />
        <SurveyActiveCircle $isActive={isActive} />
      </SurveyIconContainer>
      <SurveyText>
        Filter {isActive ? `(${count})` : ''}
      </SurveyText>
    </SurveyButton>
  );
}

function renderSurveyResponseVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count }: VariantProps) {
  const dotThemeColor = hasCustomColor ? themeColor : 'rgb(40, 170, 225)';

  return (
    <SurveyResponseButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={className}>
      <SurveyResponseIconContainer>
        <StyledFilterIcon
          height={20}
          width={20}
          $analysisVariant={true} />
        <SurveyResponseActiveCircle
          $isActive={isActive}
          $themeColor={dotThemeColor} />
      </SurveyResponseIconContainer>
      <SurveyResponseDivider />
      <SurveyResponseText>
        Filter {isActive ? `(${count})` : ''}
      </SurveyResponseText>
    </SurveyResponseButton>
  );
}

function renderChartVariant({ ref, onClick, disabled, ariaLabel, type, className, isActive, hasCustomColor, themeColor, count }: VariantProps) {
  return (
    <ChartButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={isActive ? 'active' : className}
      $isActive={isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={themeColor}>
      <ChartIconContainer
        $isActive={isActive}
        $hasCustomColor={hasCustomColor}>
        <StyledFilterIcon
          height={20}
          width={20}
          $isActive={isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={themeColor} />
        <ChartActiveCircle
          className={isActive ? 'active' : ''}
          $isActive={isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={themeColor} />
      </ChartIconContainer>
      {isActive && <ChartDivider />}
      <ChartText
        $isActive={isActive}
        $hasCustomColor={hasCustomColor}>
        {isActive ? `(${count})` : ''}
      </ChartText>
    </ChartButton>
  );
}

FilterButton.displayName = 'FilterButton';

// Analysis variant styles
const AnalysisButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  background: 'var(--gray-xl)',
  color: $themeColor || 'var(--gray-d)',
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  border: '1px solid rgb(211, 211, 211)',
  padding: '8px 12px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    opacity: 0.8,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const AnalysisIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  marginRight: '8px',
});

const AnalysisActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$themeColor'),
})<{
  $isActive?: boolean;
  $themeColor?: string;
}>(({ $isActive, $themeColor }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: $themeColor || 'rgb(40, 170, 225)',
  right: '-2px',
  top: '-2px',
  border: '2px solid var(--gray-xl)',
  display: $isActive ? 'block' : 'none',
}));

const AnalysisDivider = styled('div', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  width: '1px',
  height: '16px',
  backgroundColor: $themeColor || 'var(--gray-d)',
  marginRight: '8px',
}));

const AnalysisText = styled('span', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: $themeColor || 'var(--gray-d)',
}));

// Chart variant styles (from Chat.Chart.Filter.Button)
const ChartButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ theme, $isActive, $hasCustomColor, $themeColor }) => {
  const _borderColor = $hasCustomColor ? 'var(--gray-m)' : 'var(--gray-m)';
  const backgroundColor = $hasCustomColor ? 'var(--gray-xl)' : (theme?.palette?.white?.main || '#fff');
  const textColor = $hasCustomColor ? 'var(--gray-d)' : 'var(--gray-d)';

  return {
    display: 'flex',
    alignItems: 'center',
    background: backgroundColor,
    padding: '8px 8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: '14px',
    transition: 'all 0.2s ease',
    outline: 'none',
    borderRadius: '6px',
    color: textColor,
    whiteSpace: 'nowrap',
    border: 'none',
    '&.active': {
      background: $hasCustomColor ? 'var(--gray-l)' : 'var(--blue-l)',
      borderColor: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-m)',
      color: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-d)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      color: 'var(--gray-l)',
      boxShadow: 'none',
      '&:active, &.active': {
        background: 'none',
        color: 'inherit',
      },
    },
  };
});

const ChartIconContainer = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
}>({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: '20px',
  paddingRight: '4px',
  marginRight: '0px',
});

const ChartActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ $isActive, $hasCustomColor, $themeColor }) => {
  const circleColor = $hasCustomColor ? ($themeColor || 'var(--pri-02)') : 'var(--pri-02)';

  return {
    position: 'absolute',
    height: '6px',
    width: '6px',
    borderRadius: '8px',
    backgroundColor: circleColor,
    right: '5px',
    top: '0px',
    border: `2px solid var(--gray-xl)`,
    display: 'none',
    '&.active': {
      display: 'inline',
    },
  };
});

const ChartText = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
}>(({ $isActive, $hasCustomColor }) => {
  const textColor = $hasCustomColor ? 'var(--gray-d)' : 'inherit';

  return {
    fontSize: '14px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    color: textColor,
  };
});

const ChartDivider = styled.div({
  width: '1px',
  height: '16px',
  backgroundColor: 'var(--gray-m)',
  marginLeft: '4px',
  marginRight: '4px',
});

// Survey variant styles (simplified version)
const SurveyButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ theme, $isActive, $hasCustomColor, $themeColor }) => ({
  display: 'flex',
  alignItems: 'center',
  background: theme?.palette?.white?.main || '#fff',
  padding: '8px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '14px',
  transition: 'all 0.2s ease',
  outline: 'none',
  borderRadius: '6px',
  border: `1px solid ${$isActive && $hasCustomColor ? $themeColor : 'var(--gray-m)'}`,
  color: $isActive && $hasCustomColor ? $themeColor : 'inherit',
  '&:hover': {
    opacity: 0.8,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const SurveyIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  marginRight: '8px',
});

const SurveyActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive'),
})<{
  $isActive?: boolean;
}>(({ $isActive }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: 'var(--pri-02)',
  right: '-2px',
  top: '-2px',
  border: '1px solid white',
  display: $isActive ? 'block' : 'none',
}));

const SurveyText = styled.span({
  fontSize: '14px',
  fontWeight: 500,
});

// Survey Response variant styles (matches analysis variant styling)
const SurveyResponseButton = styled.button({
  background: 'var(--gray-xl)',
  color: 'var(--gray-d)',
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  border: '1px solid rgb(211, 211, 211)',
  padding: '8px 12px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s ease',
  outline: 'none',
  '&:hover': {
    opacity: 0.9,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const SurveyResponseIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: '20px',
});

const SurveyResponseActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$themeColor'),
})<{
  $isActive?: boolean;
  $themeColor?: string;
}>(({ $isActive, $themeColor }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: $themeColor || 'rgb(40, 170, 225)',
  right: '-2px',
  top: '-2px',
  border: '2px solid var(--gray-xl)',
  display: $isActive ? 'block' : 'none',
}));

const SurveyResponseDivider = styled.div({
  width: '1px',
  height: '16px',
  backgroundColor: 'var(--gray-d)',
});

const SurveyResponseText = styled.span({
  fontSize: '1rem',
  fontWeight: 500,
  color: 'var(--gray-d)',
});

// Shared icon styles
const StyledFilterIcon = styled(FilterIcon, {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor', '$analysisVariant'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
  $analysisVariant?: boolean;
}>(({ $isActive, $hasCustomColor, $themeColor, $analysisVariant }) => {
  // For analysis variant, use gray-d unless theme color is provided
  if ($analysisVariant) {
    const iconColor = $themeColor || 'var(--gray-d)';
    return {
      color: iconColor,
      stroke: iconColor,
      fill: 'none',
    };
  }

  // For other variants, use existing logic
  const iconColor = 'var(--gray-d)';
  return {
    color: $themeColor || iconColor,
    stroke: $themeColor || iconColor,
    fill: 'none',
  };
});
